import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaTimes, FaUserFriends, FaUtensils, FaBroom,
  FaConciergeBell, FaHandsHelping, FaInfoCircle,
  FaMapMarkerAlt, FaSpinner
} from 'react-icons/fa';
import { useApp } from '../App';
import './RequestModal.css';

/* Service → icon + descriptive hint */
const SERVICES = [
  { key: 'Chef',          icon: <FaUtensils />,       hint: 'Personal chef for meals, events or parties' },
  { key: 'Housekeeper',   icon: <FaBroom />,          hint: 'Home organization, cleaning and daily upkeep' },
  { key: 'Receptionist',  icon: <FaConciergeBell />,  hint: 'Front desk & guest services for hotels, offices' },
  { key: 'Cleaner',       icon: <FaHandsHelping />,   hint: 'Deep cleaning for residential / commercial spaces' }
];

/* Event types per service */
const EVENT_TYPES_BY_SERVICE = {
  Chef: ['Party', 'Wedding', 'Home Dining', 'Private Dining', 'Corporate Event', 'Other'],
  Housekeeper: ['Home', 'Weekly Cleaning', 'Deep Cleaning', 'Move-in / Move-out', 'Other'],
  Receptionist: ['Hotel', 'Office', 'Event', 'Front Desk Shift', 'Other'],
  Cleaner: ['Residential', 'Commercial Office', 'Post-Construction', 'Deep Cleaning', 'Other']
};

function RequestModal({ onClose }) {
  const {
    userEmail, setUserEmail,
    preselectedPro, setPreselectedPro,
    preselectedService, setPreselectedService
  } = useApp();

  const [professionals, setProfessionals] = useState([]);
  const [step, setStep] = useState(userEmail ? 'form' : 'email');
  const [email, setEmail] = useState(userEmail || '');

  /* ============================================
     INITIAL FORM STATE
     Priority:
       1. preselectedPro  → use that pro's role
       2. preselectedService → use that service
       3. Default → Chef / Party
     ============================================ */
  const [form, setForm] = useState(() => {
    const base = {
      full_name: '', email: userEmail || '', phone: '',
      service_type: 'Chef',
      event_type: 'Party',
      event_date: '', event_time: '',
      guests: '',
      number_of_professionals: '',
      location: '',
      professional_id: '',
      notes: ''
    };

    /* Priority 1: professional preselected */
    if (preselectedPro) {
      const role = preselectedPro.role || 'Chef';
      const validEvents = EVENT_TYPES_BY_SERVICE[role] || ['Other'];
      base.service_type = role;
      base.event_type = validEvents[0];
      base.professional_id = String(preselectedPro.id);
      return base;
    }

    /* Priority 2: service preselected */
    if (preselectedService) {
      const validEvents = EVENT_TYPES_BY_SERVICE[preselectedService] || ['Other'];
      base.service_type = preselectedService;
      base.event_type = validEvents[0];
      return base;
    }

    return base;
  });

  const [selectedPro, setSelectedPro] = useState(preselectedPro || null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState('');

  /* ============================================
     LOAD PROFESSIONALS
     ============================================ */
  useEffect(() => {
    axios.get('https://api.hospifylink.com/api/professionals')
      .then(r => setProfessionals(r.data))
      .catch(() => {});

    if (userEmail) checkEmail(userEmail);
    // eslint-disable-next-line
  }, []);

  /* ============================================
     RESET EVENT/PRO WHEN SERVICE CHANGES
     Skip during initial mount if preselecting that exact service
     ============================================ */
  useEffect(() => {
    if (preselectedPro && String(preselectedPro.role) === String(form.service_type)) {
      return;
    }
    if (
      preselectedService &&
      String(preselectedService) === String(form.service_type) &&
      !preselectedPro
    ) {
      return;
    }

    const valid = EVENT_TYPES_BY_SERVICE[form.service_type] || ['Other'];
    if (!valid.includes(form.event_type)) {
      setForm(f => ({ ...f, event_type: valid[0], professional_id: '' }));
    } else {
      setForm(f => ({ ...f, professional_id: '' }));
    }
    setSelectedPro(null);
    // eslint-disable-next-line
  }, [form.service_type]);

  /* ============================================
     PREFILL EXISTING USER
     ============================================ */
  const checkEmail = async (em) => {
    try {
      const r = await axios.post('/api/users/lookup', { email: em });
      if (r.data) {
        setForm(f => ({
          ...f,
          full_name: r.data.full_name || '',
          email: r.data.email || em,
          phone: r.data.phone || '',
          location: f.location || r.data.address || ''
        }));
      } else {
        setForm(f => ({ ...f, email: em }));
      }
      setUserEmail(em);
    } catch {
      setForm(f => ({ ...f, email: em }));
    }
    setStep(prev => (prev === 'form' ? prev : 'form'));
  };

  const submitEmail = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    checkEmail(email.trim());
  };

  /* ============================================
     SUBMIT REQUEST
     ============================================ */
  const submitForm = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        user_email: form.email,
        address: form.location
      };
      const r = await axios.post('/api/requests', payload);
      setUserEmail(form.email);

      /* Clear both preselections */
      if (setPreselectedPro) setPreselectedPro(null);
      if (setPreselectedService) setPreselectedService(null);

      setSuccess(r.data);
    } catch {
      alert('Failed to submit request');
    }
    setLoading(false);
  };

  /* ============================================
     CLOSE MODAL — clear both preselections
     ============================================ */
  const handleClose = () => {
    if (setPreselectedPro) setPreselectedPro(null);
    if (setPreselectedService) setPreselectedService(null);
    onClose();
  };

  /* ============================================
     LIVE LOCATION
     ============================================ */
  const useLiveLocation = () => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser.');
      return;
    }
    setLocating(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const fallback = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;

        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`;
          const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
          const data = await res.json();
          const a = data.address || {};

          const parts = [
            a.road || a.pedestrian || a.neighbourhood,
            a.suburb || a.village || a.town,
            a.city || a.county,
            a.state,
            a.country
          ].filter(Boolean);

          const pretty = parts.length ? parts.join(', ') : (data.display_name || fallback);
          setForm(f => ({ ...f, location: pretty }));
        } catch {
          setForm(f => ({ ...f, location: fallback }));
        }

        setLocating(false);
      },
      (err) => {
        setLocating(false);
        if (err.code === 1) setLocationError('Permission denied. Enter location manually.');
        else if (err.code === 2) setLocationError('Location unavailable. Enter manually.');
        else setLocationError('Could not get location. Enter manually.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const filteredProfessionals = professionals.filter(p => p.role === form.service_type);
  const eventTypes = EVENT_TYPES_BY_SERVICE[form.service_type] || ['Other'];
  const currentService = SERVICES.find(s => s.key === form.service_type) || SERVICES[0];

  const showGuests = form.service_type === 'Chef' || form.service_type === 'Receptionist';
  const teamLabel =
    form.service_type === 'Chef' ? 'Number of Chefs' :
    form.service_type === 'Housekeeper' ? 'Number of Housekeepers' :
    form.service_type === 'Receptionist' ? 'Number of Receptionists' :
    'Number of Cleaners';

  return (
    <div className="reqmodal-root" onClick={handleClose}>
      <div className="reqmodal-box" onClick={e => e.stopPropagation()}>
        <button className="reqmodal-close" onClick={handleClose}><FaTimes /></button>

        <div className="reqmodal-scroll">
          {success ? (
            /* ============== SUCCESS ============== */
            <div className="reqmodal-success">
              <div className="reqmodal-success-icon">✓</div>
              <h2>Request Submitted!</h2>
              <p>Your {form.service_type.toLowerCase()} request has been received. Save your request number:</p>
              <div className="reqmodal-number">{success.request_number}</div>
              <p className="reqmodal-hint">
                Track your request anytime using this number or your email.
              </p>
              <button className="lca-btn-primary" onClick={handleClose}>Done</button>
            </div>
          ) : step === 'email' ? (
            /* ============== EMAIL STEP ============== */
            <form onSubmit={submitEmail} className="reqmodal-email-step">
              <h2>Make a Request</h2>
              <p>
                Enter your email to get started. If you're returning, we'll load your info automatically.
              </p>
              {preselectedPro && (
                <div className="reqmodal-pro-preview reqmodal-pro-preview-compact">
                  <img
                    src={preselectedPro.image || 'https://via.placeholder.com/100'}
                    alt={preselectedPro.name}
                    className="reqmodal-pro-img"
                  />
                  <div className="reqmodal-pro-info">
                    <div className="reqmodal-pro-name">
                      {preselectedPro.name}
                      <span className="reqmodal-pro-role">{preselectedPro.role}</span>
                    </div>
                    {preselectedPro.specialty && (
                      <div className="reqmodal-pro-line">
                        <b>Specialty:</b> {preselectedPro.specialty}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {preselectedService && !preselectedPro && (
                <div className="reqmodal-service-badge">
                  Service selected: <b>{preselectedService}</b>
                </div>
              )}
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus
              />
              <button type="submit" className="lca-btn-primary">Continue</button>
            </form>
          ) : (
            /* ============== REQUEST FORM ============== */
            <form onSubmit={submitForm} className="reqmodal-form">
              <h2>
                {(preselectedService || preselectedPro)
                  ? `Request a ${form.service_type}`
                  : 'Hospitality Request Form'}
              </h2>

              {/* ---------- SERVICE PICKER ---------- */}
              <label className="reqmodal-label">What service do you need?</label>
              <div className="reqmodal-services">
                {SERVICES.map(s => (
                  <button
                    type="button"
                    key={s.key}
                    className={`reqmodal-service-card ${form.service_type === s.key ? 'reqmodal-service-active' : ''}`}
                    onClick={() => setForm(f => ({ ...f, service_type: s.key }))}
                  >
                    <span className="reqmodal-service-icon">{s.icon}</span>
                    <span className="reqmodal-service-name">{s.key}</span>
                  </button>
                ))}
              </div>
              <p className="reqmodal-service-hint">
                <FaInfoCircle /> {currentService.hint}
              </p>

              {/* ---------- PERSONAL DETAILS ---------- */}
              <div className="reqmodal-grid">
                <input required placeholder="Full Name" value={form.full_name}
                  onChange={e => setForm({ ...form, full_name: e.target.value })} />
                <input required type="email" placeholder="Email" value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })} />
                <input required placeholder="Phone" value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })} />

                {/* ---------- EVENT / OCCASION ---------- */}
                <select value={form.event_type}
                  onChange={e => setForm({ ...form, event_type: e.target.value })}>
                  {eventTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>

                <input type="date" required value={form.event_date}
                  onChange={e => setForm({ ...form, event_date: e.target.value })} />
                <input type="time" value={form.event_time}
                  onChange={e => setForm({ ...form, event_time: e.target.value })} />

                {showGuests && (
                  <input type="number" min="1" placeholder="Number of Guests"
                    value={form.guests}
                    onChange={e => setForm({ ...form, guests: e.target.value })} />
                )}

                <div className="reqmodal-chefs-field">
                  <FaUserFriends className="reqmodal-chefs-icon" />
                  <input type="number" min="1" max="20" required
                    placeholder={teamLabel}
                    value={form.number_of_professionals}
                    onChange={e => setForm({ ...form, number_of_professionals: e.target.value })} />
                </div>

                {/* ---------- LOCATION ---------- */}
                <div className="reqmodal-location-field">
                  <div className="reqmodal-location-input-wrap">
                    <FaMapMarkerAlt className="reqmodal-location-icon" />
                    <input
                      required
                      placeholder="Location / Address — or use live location"
                      value={form.location}
                      onChange={e => {
                        setForm({ ...form, location: e.target.value });
                        setLocationError('');
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className="reqmodal-location-btn"
                    onClick={useLiveLocation}
                    disabled={locating}
                    title="Use my current location"
                  >
                    {locating
                      ? <><FaSpinner className="reqmodal-spin" /> <span className="reqmodal-loc-label">Locating…</span></>
                      : <><FaMapMarkerAlt /> <span className="reqmodal-loc-label">Use Live Location</span></>}
                  </button>
                </div>
                {locationError && (
                  <p className="reqmodal-location-error">{locationError}</p>
                )}

                {/* ---------- PREFERRED PROFESSIONAL ---------- */}
                <select
                  value={form.professional_id}
                  onChange={e => {
                    const id = e.target.value;
                    setForm({ ...form, professional_id: id });
                    const found = filteredProfessionals.find(p => String(p.id) === String(id));
                    setSelectedPro(found || null);
                  }}
                >
                  <option value="">Any {form.service_type} (no preference)</option>
                  {filteredProfessionals.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.specialty ? ` — ${p.specialty}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* ---------- SELECTED PROFESSIONAL PREVIEW ---------- */}
              {selectedPro && (
                <div className="reqmodal-pro-preview">
                  <img
                    src={selectedPro.image || 'https://via.placeholder.com/100'}
                    alt={selectedPro.name}
                    className="reqmodal-pro-img"
                  />
                  <div className="reqmodal-pro-info">
                    <div className="reqmodal-pro-name">
                      {selectedPro.name}
                      <span className="reqmodal-pro-role">{selectedPro.role}</span>
                    </div>
                    {selectedPro.specialty && (
                      <div className="reqmodal-pro-line">
                        <b>Specialty:</b> {selectedPro.specialty}
                      </div>
                    )}
                    {selectedPro.category && (
                      <div className="reqmodal-pro-line">
                        <b>Category:</b> {selectedPro.category}
                      </div>
                    )}
                    {selectedPro.experience && (
                      <div className="reqmodal-pro-line">
                        <b>Experience:</b> {selectedPro.experience}
                      </div>
                    )}
                    {/* {selectedPro.hourly_rate > 0 && (
                      <div className="reqmodal-pro-line">
                        <b>Rate:</b> ${selectedPro.hourly_rate}/hour
                      </div>
                    )} */}
                    {selectedPro.bio && (
                      <div className="reqmodal-pro-bio">{selectedPro.bio}</div>
                    )}
                    <button
                      type="button"
                      className="reqmodal-pro-clear"
                      onClick={() => {
                        setSelectedPro(null);
                        setForm({ ...form, professional_id: '' });
                        if (setPreselectedPro) setPreselectedPro(null);
                      }}
                    >
                      Clear preference
                    </button>
                  </div>
                </div>
              )}

              <textarea rows="3"
                placeholder="Special Notes — menu preferences, dietary needs, schedule, etc."
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} />

              <button type="submit" className="lca-btn-primary" disabled={loading}>
                {loading ? 'Submitting...' : `Submit ${form.service_type} Request`}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default RequestModal;
